<template>
  <div class="bulk-delete-detail">
    <!-- Loading Spinner -->
    <div v-if="loading && !job" class="loading-container">
      <b-spinner variant="primary" class="loading-spinner"></b-spinner>
      <p class="mt-3 text-muted">Loading job details...</p>
    </div>

    <!-- Job Not Found -->
    <div v-else-if="!job" class="empty-state">
      <div class="empty-state-icon">
        <b-icon icon="exclamation-triangle" font-scale="4"></b-icon>
      </div>
      <h3 class="mb-2">Job Not Found</h3>
      <p class="text-muted mb-4">
        The bulk delete job you're looking for doesn't exist or has been
        removed.
      </p>
      <b-button variant="primary" to="/bulk-delete/list" size="lg">
        <b-icon icon="arrow-left" class="mr-1"></b-icon>
        Back to List
      </b-button>
    </div>

    <!-- Job Details -->
    <div v-else class="job-content">
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <b-button variant="link" to="/bulk-delete/list" class="back-button">
            <b-icon icon="arrow-left"></b-icon>
            <span class="ml-2">Back to List</span>
          </b-button>

          <div class="mt-3">
            <h1 class="page-title">{{ job.pageName || "Unknown Page" }}</h1>
            <div class="page-meta">
              <a
                :href="`https://facebook.com/${job.pageId}`"
                target="_blank"
                class="page-link"
              >
                <b-icon icon="facebook"></b-icon>
                <span class="ml-1">View on Facebook</span>
              </a>
              <span class="separator">•</span>
              <span class="job-id">Job ID: {{ job.jobId }}</span>
            </div>
          </div>
        </div>

        <div class="header-actions">
          <b-badge :variant="getStatusVariant(job.status)" class="status-badge">
            <span class="status-icon">●</span>
            {{ job.status }}
          </b-badge>

          <b-button
            variant="outline-secondary"
            size="sm"
            @click="fetchJobDetails"
            :disabled="loading"
            class="refresh-btn ml-3"
          >
            <b-icon
              icon="arrow-clockwise"
              :animation="loading ? 'spin' : ''"
            ></b-icon>
          </b-button>
        </div>
      </div>

      <!-- Job Overview Card -->
      <div class="overview-card">
        <div class="card-header">
          <h5 class="card-title">
            <b-icon icon="info-circle" class="mr-2"></b-icon>
            Job Overview
          </h5>
        </div>
        <div class="overview-grid">
          <div class="overview-item">
            <div class="overview-label">Content Type</div>
            <b-badge
              :variant="getContentTypeBadge(job.contentType)"
              class="overview-badge"
            >
              {{ job.contentType }}
            </b-badge>
          </div>
          <div class="overview-item">
            <div class="overview-label">Delete After</div>
            <div class="overview-value">{{ job.deleteAfter }} hours</div>
          </div>
          <div class="overview-item">
            <div class="overview-label">Deletion Interval</div>
            <div class="overview-value">
              {{ job.intervalMin || 2 }} - {{ job.intervalMax || 10 }} minutes
            </div>
          </div>
          <div class="overview-item">
            <div class="overview-label">Using Proxy</div>
            <b-badge
              :variant="job.useProxy !== false ? 'success' : 'secondary'"
              class="overview-badge"
            >
              {{ job.useProxy !== false ? "Yes" : "No" }}
            </b-badge>
          </div>
          <div class="overview-item">
            <div class="overview-label">Created At</div>
            <div class="overview-value">{{ formatDate(job.createdAt) }}</div>
          </div>
          <div class="overview-item">
            <div class="overview-label">Completed At</div>
            <div class="overview-value">
              {{
                job.completedAt ? formatDate(job.completedAt) : "In Progress"
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="stats-grid">
        <div class="stat-card stat-fetched">
          <div class="stat-icon-wrapper">
            <b-icon icon="cloud-download" font-scale="2"></b-icon>
          </div>
          <div class="stat-details">
            <div class="stat-value">{{ job.totalFetched }}</div>
            <div class="stat-label">Total Fetched</div>
          </div>
        </div>

        <div class="stat-card stat-scheduled">
          <div class="stat-icon-wrapper">
            <b-icon icon="clock" font-scale="2"></b-icon>
          </div>
          <div class="stat-details">
            <div class="stat-value">{{ job.totalScheduled }}</div>
            <div class="stat-label">Scheduled</div>
          </div>
        </div>

        <div class="stat-card stat-deleted">
          <div class="stat-icon-wrapper">
            <b-icon icon="check-circle-fill" font-scale="2"></b-icon>
          </div>
          <div class="stat-details">
            <div class="stat-value">{{ job.totalDeleted }}</div>
            <div class="stat-label">Deleted</div>
          </div>
        </div>

        <div class="stat-card stat-failed">
          <div class="stat-icon-wrapper">
            <b-icon icon="exclamation-triangle-fill" font-scale="2"></b-icon>
          </div>
          <div class="stat-details">
            <div class="stat-value">{{ job.totalFailed }}</div>
            <div class="stat-label">Failed</div>
          </div>
        </div>
      </div>

      <!-- Progress Card -->
      <div class="progress-card">
        <div class="card-header">
          <h5 class="card-title">
            <b-icon icon="graph-up" class="mr-2"></b-icon>
            Progress
          </h5>
        </div>
        <div class="progress-content">
          <div class="progress-stats">
            <div class="progress-stat-item">
              <span class="progress-stat-value">{{ job.totalDeleted }}</span>
              <span class="progress-stat-separator">/</span>
              <span class="progress-stat-total">{{ job.totalScheduled }}</span>
              <span class="progress-stat-label">deleted</span>
            </div>
            <div class="progress-percentage">
              {{ getProgressPercentage() }}%
            </div>
          </div>

          <div class="progress-bar-wrapper">
            <b-progress
              :value="getProgressPercentage()"
              :variant="getProgressVariant()"
              height="12px"
              class="custom-progress"
            ></b-progress>
          </div>

          <div v-if="remainingScheduled > 0" class="progress-remaining">
            <b-icon icon="clock" class="mr-1"></b-icon>
            <span>{{ remainingScheduled }} items remaining in schedule</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="actions-card" v-if="hasActions">
        <div class="card-header">
          <h5 class="card-title">
            <b-icon icon="sliders" class="mr-2"></b-icon>
            Actions
          </h5>
        </div>
        <div class="actions-content">
          <b-button
            v-if="job.status === 'Scheduled' || job.status === 'Running'"
            variant="warning"
            @click="pauseJob"
            class="action-btn"
            size="lg"
          >
            <b-icon icon="pause-fill" class="mr-2"></b-icon>
            Pause Job
          </b-button>
          <b-button
            v-if="job.status !== 'Completed' && job.status !== 'Failed'"
            variant="danger"
            @click="cancelJob"
            class="action-btn"
            size="lg"
          >
            <b-icon icon="x-circle-fill" class="mr-2"></b-icon>
            Cancel Job
          </b-button>
        </div>
      </div>

      <!-- Scheduled Content Table -->
      <div v-if="scheduledItems && scheduledItems.length > 0" class="data-card">
        <div class="card-header">
          <h5 class="card-title">
            <b-icon icon="calendar-check" class="mr-2"></b-icon>
            Scheduled Content
            <b-badge variant="primary" class="ml-2">{{
              scheduledItems.length
            }}</b-badge>
          </h5>
        </div>
        <div class="table-wrapper">
          <b-table
            :items="scheduledItems"
            :fields="tableFields"
            hover
            responsive
            class="custom-table"
          >
            <template #cell(contentId)="data">
              <a :href="data.item.link" target="_blank" class="content-link">
                <b-icon icon="link-45deg" class="mr-1"></b-icon>
                {{ data.item.contentId }}
              </a>
            </template>
            <template #cell(deleteTime)="data">
              <span class="date-text">{{
                formatDate(data.item.deleteTime)
              }}</span>
            </template>
            <template #cell(countdown)="data">
              <b-badge
                :variant="getCountdownVariant(data.item.deleteTime)"
                class="countdown-badge"
              >
                <b-icon icon="clock" class="mr-1"></b-icon>
                {{ getCountdown(data.item.deleteTime) }}
              </b-badge>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Failed Deletions Table -->
      <div
        v-if="failedItems && failedItems.length > 0"
        class="data-card error-card"
      >
        <div class="card-header">
          <h5 class="card-title text-danger">
            <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
            Failed Deletions
            <b-badge variant="danger" class="ml-2">{{
              failedItems.length
            }}</b-badge>
          </h5>
        </div>
        <div class="error-alert">
          <b-icon icon="info-circle-fill" class="mr-2"></b-icon>
          <span
            >These items could not be deleted. Check the error messages for
            details.</span
          >
        </div>
        <div class="table-wrapper">
          <b-table
            :items="failedItems"
            :fields="failedTableFields"
            hover
            responsive
            class="custom-table error-table"
          >
            <template #cell(contentId)="data">
              <a :href="data.item.link" target="_blank" class="content-link">
                <b-icon icon="link-45deg" class="mr-1"></b-icon>
                {{ data.item.contentId }}
              </a>
            </template>
            <template #cell(error)="data">
              <span class="error-message">{{ data.item.error }}</span>
            </template>
            <template #cell(failedAt)="data">
              <span class="date-text">{{
                formatDate(data.item.failedAt)
              }}</span>
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      job: null,
      loading: false,
      refreshInterval: null,
      countdownInterval: null,
      remainingScheduled: 0,
      scheduledItems: [],
      failedItems: [],
      tableFields: [
        { key: "contentId", label: "Content ID / Link" },
        { key: "deleteTime", label: "Scheduled Time" },
        { key: "countdown", label: "Time Until Delete" },
      ],
      failedTableFields: [
        { key: "contentId", label: "Content ID / Link" },
        { key: "error", label: "Error Message" },
        { key: "failedAt", label: "Failed At" },
      ],
    };
  },

  computed: {
    hasActions() {
      return (
        this.job &&
        (this.job.status === "Scheduled" ||
          this.job.status === "Running" ||
          (this.job.status !== "Completed" && this.job.status !== "Failed"))
      );
    },
  },

  mounted() {
    this.fetchJobDetails();
    this.startAutoRefresh();
    this.startCountdownTimer();
  },

  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopCountdownTimer();
  },

  watch: {
    "job.status"(newStatus) {
      if (newStatus === "Completed" || newStatus === "Failed") {
        this.stopAutoRefresh();
      }
    },
  },

  methods: {
    async fetchJobDetails() {
      const isInitialLoad = !this.job;
      if (isInitialLoad) {
        this.loading = true;
      }

      try {
        const jobId = this.$route.params.jobId;
        const response = await axios.get(`/api/bulk-delete/job/${jobId}`);
        this.job = response.data.job;
        this.remainingScheduled = response.data.remainingScheduled || 0;
        this.scheduledItems = response.data.scheduledItems || [];
        this.failedItems = response.data.failedItems || [];
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || error.message || "Unknown error";
        this.$bvToast.toast(`Failed to fetch job details: ${errorMessage}`, {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      } finally {
        this.loading = false;
      }
    },

    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        if (
          this.job &&
          (this.job.status === "Running" ||
            this.job.status === "Scheduled" ||
            this.job.status === "Fetching")
        ) {
          this.fetchJobDetails();
        }
      }, 10000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    startCountdownTimer() {
      this.countdownInterval = setInterval(() => {
        if (this.scheduledItems.length > 0) {
          this.$forceUpdate();
        }
      }, 1000);
    },

    stopCountdownTimer() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    async pauseJob() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to pause this job? Scheduled deletions will be halted.",
        {
          title: "Pause Bulk Delete Job",
          okVariant: "warning",
          okTitle: "Yes, Pause",
          cancelTitle: "Cancel",
          centered: true,
        },
      );

      if (!confirmed) return;

      try {
        await axios.post(`/api/bulk-delete/pause/${this.job.jobId}`);
        this.$bvToast.toast("Job has been paused successfully", {
          title: "Job Paused",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
        this.fetchJobDetails();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        this.$bvToast.toast(`Failed to pause job: ${errorMessage}`, {
          title: "Pause Failed",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },

    async cancelJob() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to cancel this job? This will stop all scheduled deletions and cannot be undone.",
        {
          title: "Cancel Bulk Delete Job",
          okVariant: "danger",
          okTitle: "Yes, Cancel Job",
          cancelTitle: "No, Keep Job",
          centered: true,
        },
      );

      if (!confirmed) return;

      try {
        await axios.post(`/api/bulk-delete/cancel/${this.job.jobId}`);
        this.$bvToast.toast("Job has been cancelled successfully", {
          title: "Job Cancelled",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
        this.fetchJobDetails();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        this.$bvToast.toast(`Failed to cancel job: ${errorMessage}`, {
          title: "Cancellation Failed",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },

    getStatusVariant(status) {
      const variants = {
        Fetching: "info",
        Scheduled: "primary",
        Running: "success",
        Completed: "success",
        Failed: "danger",
        Paused: "warning",
      };
      return variants[status] || "secondary";
    },

    getContentTypeBadge(contentType) {
      const badges = {
        videos: "primary",
        posts: "info",
        both: "success",
      };
      return badges[contentType] || "secondary";
    },

    getProgressPercentage() {
      if (!this.job || this.job.totalScheduled === 0) return 0;
      return Math.round(
        (this.job.totalDeleted / this.job.totalScheduled) * 100,
      );
    },

    getProgressVariant() {
      if (!this.job) return "primary";
      if (this.job.status === "Completed") return "success";
      if (this.job.status === "Failed") return "danger";
      if (this.job.status === "Paused") return "warning";
      return "primary";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleString(undefined, options);
    },

    getCountdown(deleteTime) {
      if (!deleteTime) return "Unknown";
      const now = new Date();
      const target = new Date(deleteTime);
      const diffMs = target - now;

      if (diffMs <= 0) return "Deleting now...";

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      if (days > 0) {
        return `${days}d ${hours}h`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    },

    getCountdownVariant(deleteTime) {
      if (!deleteTime) return "secondary";
      const now = new Date();
      const target = new Date(deleteTime);
      const diffMs = target - now;

      if (diffMs <= 0) return "danger";

      const hours = diffMs / (1000 * 60 * 60);
      if (hours < 1) return "danger";
      if (hours < 6) return "warning";
      return "info";
    },
  },
};
</script>

<style scoped>
.bulk-delete-detail {
  padding: 0;
  background: #f5f7fa;
  min-height: 100vh;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #374151;
}

.loading-spinner {
  width: 4rem;
  height: 4rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #374151;
  text-align: center;
  padding: 2rem;
}

.empty-state-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  color: #f59e0b;
}

.empty-state h3 {
  color: #111827;
  font-weight: 600;
}

.empty-state p {
  color: #6b7280;
}

.job-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.header-content {
  flex: 1;
  min-width: 300px;
}

.back-button {
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  padding: 0;
  transition: color 0.2s;
}

.back-button:hover {
  color: #374151;
  text-decoration: none;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.page-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.95rem;
  color: #6b7280;
}

.page-link {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}

.page-link:hover {
  color: #2563eb;
  text-decoration: none;
}

.separator {
  color: #d1d5db;
}

.job-id {
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-badge {
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-icon {
  font-size: 0.75rem;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.refresh-btn {
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}

.overview-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.card-header {
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.overview-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.overview-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.overview-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.overview-badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-transform: capitalize;
  align-self: flex-start;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: default;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-fetched .stat-icon-wrapper {
  background: #3b82f6;
  color: white;
}

.stat-scheduled .stat-icon-wrapper {
  background: #f59e0b;
  color: white;
}

.stat-deleted .stat-icon-wrapper {
  background: #10b981;
  color: white;
}

.stat-failed .stat-icon-wrapper {
  background: #ef4444;
  color: white;
}

.stat-details {
  flex: 1;
}

.stat-value {
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.95rem;
  color: #6b7280;
  font-weight: 500;
}

.progress-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.progress-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-stat-item {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 1.5rem;
}

.progress-stat-value {
  font-weight: 700;
  color: #111827;
}

.progress-stat-separator {
  color: #d1d5db;
}

.progress-stat-total {
  font-weight: 600;
  color: #6b7280;
}

.progress-stat-label {
  font-size: 1rem;
  color: #9ca3af;
  margin-left: 0.25rem;
}

.progress-percentage {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
}

.progress-bar-wrapper {
  margin: 0.5rem 0;
}

.custom-progress {
  border-radius: 50px;
  overflow: hidden;
  background-color: #f3f4f6;
}

.custom-progress >>> .progress-bar {
  border-radius: 50px;
  transition: width 0.6s ease;
}

.progress-remaining {
  font-size: 0.95rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.actions-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.actions-content {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 12px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.data-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.error-card {
  border-left: 4px solid #ef4444;
}

.error-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #991b1b;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.table-wrapper {
  overflow-x: auto;
  margin-top: 1rem;
}

.custom-table {
  margin-bottom: 0;
}

.custom-table >>> thead th {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-top: none;
  border-bottom: 2px solid #e5e7eb;
  padding: 1rem;
}

.custom-table >>> tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
}

.custom-table >>> tbody tr:hover {
  background: #f9fafb;
}

.content-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}

.content-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.date-text {
  color: #6b7280;
  font-size: 0.95rem;
}

.countdown-badge {
  font-size: 0.95rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  display: block;
  max-width: 400px;
  word-wrap: break-word;
}

.badge-warning {
  background: #f59e0b;
  color: white;
}

.badge-danger {
  background: #ef4444;
  color: white;
}

.badge-success {
  background: #10b981;
  color: white;
}

.badge-info {
  background: #3b82f6;
  color: white;
}

.badge-primary {
  background: #8b5cf6;
  color: white;
}
</style>
